import React, { useState } from 'react';
import { VCCheckGlyph } from './glyphs';
import { t } from '../i18n';
import type { IntakeData } from './Stage02IntakeFlow';

// ── SVG icons ────────────────────────────────────────────────────────────────

const BackChevron = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

// ── Phone helpers (moved here with the contact form) ──────────────────────────

const PHONE_PREFIXES = [
  '+972', '+971', '+966', '+91', '+61', '+55', '+52',
  '+44', '+49', '+39', '+34', '+33', '+31', '+32', '+41', '+90', '+86', '+7', '+1',
].sort((a, b) => b.length - a.length);

// Prefix options shown in the country-code select — flag emojis removed per the
// Gen-2.0 zero-emoji rule.
const PHONE_PREFIX_OPTIONS = [
  '+972', '+44', '+1', '+33', '+49', '+39', '+34', '+31', '+32', '+41',
  '+971', '+966', '+90', '+7', '+86', '+91', '+55', '+52', '+61',
];

function splitPhoneForDisplay(fullPhone: string): { prefix: string; national: string } {
  for (const prefix of PHONE_PREFIXES) {
    if (fullPhone.startsWith(prefix)) {
      return { prefix, national: fullPhone.slice(prefix.length) };
    }
  }
  return { prefix: '+972', national: fullPhone };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface StageContactProps {
  clinicConfig: {
    brand_name: string;
  };
  /** Full intake data captured so far (profile + Q1–Q4); contact merges in here. */
  initialData: IntakeData;
  /** Called with the merged intake data (contact fields populated) on submit. */
  onAdvance: (data: IntakeData) => void;
  onBack: () => void;
}

export const StageContact = ({ clinicConfig, initialData, onAdvance, onBack }: StageContactProps) => {
  const initialPhone = splitPhoneForDisplay(initialData.phone || '');

  const [contact, setContact] = useState({
    first_name: initialData.first_name,
    email: initialData.email,
    phone: initialPhone.national,
  });
  const [contactErrors, setContactErrors] = useState({ first_name: '', email: '', phone: '' });
  const [phonePrefix, setPhonePrefix] = useState(initialPhone.prefix);

  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const update = (patch: Partial<typeof contact>) =>
    setContact((prev) => ({ ...prev, ...patch }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    update({ [name]: value } as Partial<typeof contact>);
    if (contactErrors[name as keyof typeof contactErrors]) {
      setContactErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'first_name' && !value.trim()) {
      setContactErrors((prev) => ({ ...prev, first_name: t('contact.error.firstName') }));
    } else if (name === 'email' && !validateEmail(value)) {
      setContactErrors((prev) => ({
        ...prev,
        email: t('contact.error.email'),
      }));
    } else if (name === 'phone' && (!value.trim() || value.trim().length < 6)) {
      setContactErrors((prev) => ({ ...prev, phone: t('contact.error.phone') }));
    } else {
      setContactErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = { first_name: '', email: '', phone: '' };
    let valid = true;
    if (!contact.first_name.trim()) {
      errs.first_name = t('contact.error.firstName');
      valid = false;
    }
    if (!validateEmail(contact.email)) {
      errs.email = t('contact.error.email');
      valid = false;
    }
    if (!contact.phone.trim() || contact.phone.trim().length < 6) {
      errs.phone = t('contact.error.phone');
      valid = false;
    }
    setContactErrors(errs);
    if (valid) {
      onAdvance({
        ...initialData,
        first_name: contact.first_name,
        email: contact.email,
        phone: phonePrefix + contact.phone,
      });
    }
  };

  const isSubmitDisabled =
    !contact.first_name.trim() ||
    !validateEmail(contact.email) ||
    !contact.phone.trim() ||
    contact.phone.trim().length < 6;

  return (
    <div className="vc-frame stage-contact">
      <style>{`
        /* Contact stage — stage-specific layout only.
           Shared chamber vocabulary → widget-components.css */
        .stage-contact .vc-contact-fields {
          display: flex;
          flex-direction: column;
          gap: var(--sp-5);
          margin: var(--sp-7) var(--sp-6) 0;
        }
        .stage-contact .vc-cta-zone {
          margin-top: auto;
          padding-top: var(--sp-6);
          padding-bottom: var(--sp-6);
        }
        .stage-contact .substep-content {
          display: flex;
          flex-direction: column;
          flex: 1;
          animation: vc-substep-enter var(--dur-slow) var(--ease-entrance) both;
        }
        @keyframes vc-substep-enter {
          from { opacity: 0; transform: translateY(var(--sp-2)); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .stage-contact .substep-content {
            animation: vc-screen-fade var(--dur-base) var(--ease-standard) both;
          }
        }
      `}</style>

      <div className="vc-head">
        <button
          className="vc-head-btn"
          onClick={onBack}
          aria-label={t('contact.backAria')}
        >
          <BackChevron />
        </button>
        <span className="vc-steplabel">{t('contact.step')}</span>
        <span className="vc-head-btn vc-head-btn--ghost" aria-hidden="true"></span>
      </div>

      <div className="substep-content">
        <h2 className="vc-title" role="heading" aria-level={2}>
          {t('contact.title.line1')}<br />{t('contact.title.line2Pre')}<em>{t('contact.title.line2Em')}</em>
        </h2>
        <p className="vc-sub">{t('contact.sub')}</p>

        <form
          onSubmit={handleSubmit}
          aria-label={t('contact.formAria')}
          style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
        >
          <div className="vc-contact-fields">
            <div className="vc-field">
              <label htmlFor="first_name" className="vc-fieldlabel">
                {t('contact.firstName')}
              </label>
              <div className="vc-input-wrap">
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  className={`vc-input${
                    contactErrors.first_name
                      ? ' is-error'
                      : contact.first_name
                      ? ' is-valid'
                      : ''
                  }`}
                  placeholder={t('contact.firstNamePlaceholder')}
                  autoComplete="given-name"
                  aria-required="true"
                  aria-invalid={!!contactErrors.first_name}
                  aria-describedby={contactErrors.first_name ? 'fn-error' : undefined}
                  value={contact.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {!contactErrors.first_name && contact.first_name && (
                  <span className="vc-valid-mark" aria-hidden="true">
                    <VCCheckGlyph size={16} strokeWidth={2} />
                  </span>
                )}
              </div>
              {contactErrors.first_name && (
                <p id="fn-error" role="alert" className="vc-help vc-help--error">
                  {contactErrors.first_name}
                </p>
              )}
            </div>

            <div className="vc-field">
              <label htmlFor="email" className="vc-fieldlabel">
                {t('contact.email')}
              </label>
              <div className="vc-input-wrap">
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`vc-input${
                    contactErrors.email
                      ? ' is-error'
                      : contact.email && validateEmail(contact.email)
                      ? ' is-valid'
                      : ''
                  }`}
                  placeholder={t('contact.emailPlaceholder')}
                  autoComplete="email"
                  aria-required="true"
                  aria-invalid={!!contactErrors.email}
                  aria-describedby={contactErrors.email ? 'email-error' : undefined}
                  value={contact.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {!contactErrors.email && contact.email && validateEmail(contact.email) && (
                  <span className="vc-valid-mark" aria-hidden="true">
                    <VCCheckGlyph size={16} strokeWidth={2} />
                  </span>
                )}
              </div>
              {contactErrors.email ? (
                <p id="email-error" role="alert" className="vc-help vc-help--error">
                  {contactErrors.email}
                </p>
              ) : (
                <p className="vc-help">{t('contact.emailHelp')}</p>
              )}
            </div>

            <div className="vc-field">
              <label htmlFor="phone" className="vc-fieldlabel">
                {t('contact.phone')}
              </label>
              <div className="vc-phone">
                <select
                  id="country_code"
                  name="country_code"
                  className="cc"
                  aria-label={t('contact.countryCodeAria')}
                  value={phonePrefix}
                  onChange={(e) => setPhonePrefix(e.target.value)}
                >
                  {PHONE_PREFIX_OPTIONS.map((prefix) => (
                    <option key={prefix} value={prefix}>
                      {prefix}
                    </option>
                  ))}
                </select>
                <div className="vc-input-wrap">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className={`vc-input${
                      contactErrors.phone
                        ? ' is-error'
                        : contact.phone && contact.phone.trim().length >= 6
                        ? ' is-valid'
                        : ''
                    }`}
                    placeholder={t('contact.phonePlaceholder')}
                    autoComplete="tel"
                    aria-required="true"
                    aria-invalid={!!contactErrors.phone}
                    aria-describedby={contactErrors.phone ? 'phone-error' : undefined}
                    value={contact.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {!contactErrors.phone && contact.phone && contact.phone.trim().length >= 6 && (
                    <span className="vc-valid-mark" aria-hidden="true">
                      <VCCheckGlyph size={16} strokeWidth={2} />
                    </span>
                  )}
                </div>
              </div>
              {contactErrors.phone && (
                <p id="phone-error" role="alert" className="vc-help vc-help--error">
                  {contactErrors.phone}
                </p>
              )}
            </div>
          </div>

          <div className="vc-cta-zone">
            <button
              type="submit"
              className="vc-btn vc-btn--primary"
              disabled={isSubmitDisabled}
              aria-disabled={isSubmitDisabled}
            >
              {t('contact.cta')}
            </button>
            <p className="vc-legal" style={{ marginTop: 'var(--sp-4)' }}>
              {t('contact.legal', { brand: clinicConfig.brand_name })}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
